// Pedir y esperar los guiones de la tanda (ADR-2026-10-30-videos-cortos-guiones-antes-del-lote):
// `POST /v1/videos/masivo/guiones` encola un job gratis por producto y responde los `execution_id`;
// acá se polea `GET …?ids=` cada 2 s y cada guion que llega se vuelca al plan al instante, como
// hace el escritor del Catálogo. Lo puro (el cuerpo, el volcado) va primero, con tests.
import { fallarGuion, normalizarGuion, volcarGuion, vozDelProducto, type PlanDeVideos, type ProductoDeLaTanda } from './plan';
import { pedirGuiones, verGuiones, type EjecucionDeGuion, type PedidoDeGuiones } from './videosCortosApi';

/**
 * El pedido al escritor: un producto por cada uno pedido, con sus notas como indicación propia y la indicación
 * general de la tanda (más la puntual de esta reescritura, si la hay). La voz es la de la tanda: con una voz distinta
 * por producto el director sólo necesita saber si escribe con o sin voz, y para eso alcanza la de la tanda si no es
 * null… salvo que la del producto sea null y la de la tanda no (o al revés): entonces va en un pedido aparte. Puro.
 */
export function cuerposParaElEscritor(plan: PlanDeVideos, productos: ProductoDeLaTanda[], indicacion?: string): PedidoDeGuiones[] {
  const general = [plan.indicacion.trim(), indicacion?.trim() ?? ''].filter(Boolean).join('\n').slice(0, 500);
  const conVoz: PedidoDeGuiones['productos'] = [];
  const sinVoz: PedidoDeGuiones['productos'] = [];
  for (const p of productos) {
    const entrada = { variante_id: p.varianteId, ...(p.notas?.trim() ? { indicacion: p.notas.trim().slice(0, 600) } : {}) };
    (vozDelProducto(plan, p) === null ? sinVoz : conVoz).push(entrada);
  }
  const vozDeLaTanda = plan.voz === null ? 'auto' : plan.voz;
  const out: PedidoDeGuiones[] = [];
  if (conVoz.length) out.push({ productos: conVoz, voz: vozDeLaTanda, ...(general ? { indicacion: general } : {}) });
  if (sinVoz.length) out.push({ productos: sinVoz, voz: null, ...(general ? { indicacion: general } : {}) });
  return out;
}

/** Lo que devolvió una ejecución → el plan (guion volcado o fallo con motivo). Puro. */
export function volcarEjecucion(plan: PlanDeVideos, varianteId: string, e: EjecucionDeGuion): { plan: PlanDeVideos; ok: boolean; motivo?: string } {
  if (e.status === 'succeeded') {
    const guion = normalizarGuion(e.output?.guion);
    if (!guion) return { plan: fallarGuion(plan, varianteId, 'el escritor no devolvió el guion'), ok: false, motivo: 'el escritor no devolvió el guion' };
    return { plan: volcarGuion(plan, varianteId, guion), ok: true };
  }
  const motivo = e.error ?? 'el escritor falló';
  return { plan: fallarGuion(plan, varianteId, motivo), ok: false, motivo };
}

const POLL_MS = 2_000;
/** Cuánto se espera: proporcional (20 s por producto; el director tarda 30-60 s y salen de a 10), entre 3 y 15 minutos. */
export const esperaMaximaMs = (productos: number): number => Math.min(900_000, Math.max(180_000, productos * 20_000));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface ResultadoDeEscritura {
  plan: PlanDeVideos;
  escritos: number;
  fallidos: Array<{ producto: string; motivo: string }>;
}

/**
 * Manda los productos al escritor y espera, volcando cada guion en cuanto llega (`onProducto` recibe el plan
 * actualizado). Resuelve cuando terminaron todos o al agotar la espera (lo que quedó abierto se marca como fallo).
 * Nunca tira: los errores vuelven en `fallidos`.
 */
export async function escribirGuiones(p: {
  plan: PlanDeVideos;
  productos: ProductoDeLaTanda[];
  indicacion?: string;
  onProducto: (plan: PlanDeVideos) => void;
  signal?: AbortSignal;
}): Promise<ResultadoDeEscritura> {
  let plan = p.plan;
  const fallidos: Array<{ producto: string; motivo: string }> = [];
  const nombreDe = (varianteId: string) => plan.productos.find((x) => x.varianteId === varianteId)?.nombre ?? 'un producto';
  const fallar = (varianteId: string, motivo: string) => {
    plan = fallarGuion(plan, varianteId, motivo);
    fallidos.push({ producto: nombreDe(varianteId), motivo });
  };
  const cuerpos = cuerposParaElEscritor(plan, p.productos, p.indicacion);
  if (cuerpos.length === 0) return { plan, escritos: 0, fallidos };

  const productoDeEjecucion = new Map<string, string>();
  for (const cuerpo of cuerpos) {
    try {
      const r = await pedirGuiones(cuerpo);
      for (const g of r.guiones) productoDeEjecucion.set(g.execution_id, g.variante_id);
      for (const id of r.noEncolados) fallar(id, r.error ? `no se pudo pedir el guion: ${r.error}` : 'la cola está llena, reintentá en un rato');
    } catch {
      for (const x of cuerpo.productos) fallar(x.variante_id, 'no pude conectarme con el escritor');
    }
  }
  if (fallidos.length > 0) p.onProducto(plan);

  const abiertas = new Set(productoDeEjecucion.keys());
  let escritos = 0;
  const inicio = Date.now();
  const esperaMaxima = esperaMaximaMs(p.productos.length);
  while (abiertas.size > 0 && Date.now() - inicio < esperaMaxima && !p.signal?.aborted) {
    await sleep(POLL_MS);
    let ejecuciones: EjecucionDeGuion[] = [];
    try {
      ejecuciones = await verGuiones([...abiertas]);
    } catch {
      continue;
    }
    let hubo = false;
    for (const e of ejecuciones) {
      if (!abiertas.has(e.execution_id) || (e.status !== 'succeeded' && e.status !== 'failed')) continue;
      abiertas.delete(e.execution_id);
      const varianteId = productoDeEjecucion.get(e.execution_id)!;
      const r = volcarEjecucion(plan, varianteId, e);
      plan = r.plan;
      if (r.ok) escritos++;
      else fallidos.push({ producto: nombreDe(varianteId), motivo: r.motivo ?? 'el escritor falló' });
      hubo = true;
    }
    if (hubo) p.onProducto(plan);
  }
  if (abiertas.size > 0) {
    const motivo = p.signal?.aborted ? 'se canceló la escritura' : 'el escritor tardó demasiado';
    for (const e of abiertas) fallar(productoDeEjecucion.get(e)!, motivo);
    p.onProducto(plan);
  }
  return { plan, escritos, fallidos };
}
